import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, 
  Type, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  Bold, 
  Italic, 
  Underline,
  X,
  Palette,
  ChevronDown,
  ChevronUp,
  RotateCcw
} from 'lucide-react';
import { TypographyDropdown } from './TypographyDropdown';
import { useBrand } from '@/context/BrandContext';
import { TextToolbar } from './TextToolbar';
import { useShortcodes } from '@/hooks/useShortcodes';

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  element?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  multiline?: boolean;
  isEditing?: boolean;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  editModeEnabled?: boolean;
  onStyleChange?: (styles: any) => void;
  initialStyles?: any;
  isGlobalDarkMode?: boolean;
  sectionBackgroundColor?: string;
  enableShortcodes?: boolean;
}

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  onChange,
  element = 'p',
  className = '',
  style = {},
  placeholder = 'Click to edit...',
  multiline = false,
  isEditing: externalIsEditing = false,
  onEditStart,
  onEditEnd,
  editModeEnabled = false,
  onStyleChange,
  initialStyles = {},
  isGlobalDarkMode = false,
  sectionBackgroundColor = '#FFFFFF',
  enableShortcodes = true
}) => {
  const { state } = useBrand();
  const { processText } = useShortcodes();
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const [isToolbarHovered, setIsToolbarHovered] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [fadeTimeout, setFadeTimeout] = useState<NodeJS.Timeout | null>(null);
  const [tempValue, setTempValue] = useState(value);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  
  // Helper function to determine if background is dark
  const isBackgroundDark = (bgColor: string): boolean => {
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  // Get default styles based on element type, dark mode, and section background
  const getDefaultStyles = (elementType: string) => {
    const isHeading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(elementType);
    const isDarkBackground = isBackgroundDark(sectionBackgroundColor);
    
    let defaultTextColor = '#333942';
    let defaultTitleColor = '#000000';
    
    if (isDarkBackground) {
      defaultTextColor = '#d4d4d4';
      defaultTitleColor = '#ffffff';
    }
    
    // Get global fonts from brand data
    const globalTitleFont = state.brandData?.globalTitleFont || 'Inter';
    const globalTextFont = state.brandData?.globalTextFont || 'Inter';
    
    const baseStyles = {
      fontFamily: isHeading ? globalTitleFont : globalTextFont,
      fontSize: getDefaultFontSize(elementType),
      textAlign: 'left',
      fontWeight: 'normal',
      fontStyle: 'normal',
      textDecoration: 'none',
      textTransform: 'none',
      lineHeight: 1.2,
      letterSpacing: '0em',
      color: isHeading ? defaultTitleColor : defaultTextColor,
      backgroundColor: 'transparent'
    };

    if (elementType === 'h1') {
      return {
        ...baseStyles,
        textTransform: 'uppercase',
        lineHeight: 1.0,
        fontWeight: 'bold'
      };
    }

    return baseStyles;
  };

  const [textStyles, setTextStyles] = useState({
    ...getDefaultStyles(element),
    ...initialStyles
  });
  
  // Store original styles when component mounts
  const [originalStyles] = useState(() => ({
    ...getDefaultStyles(element),
    ...initialStyles
  }));
  
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const elementRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textElementRef = useRef<HTMLElement>(null);

  function getDefaultFontSize(elementType: string): number {
    const headingSizes = {
      h1: 128,
      h2: 96,
      h3: 72,
      h4: 56,
      h5: 48,
      h6: 36
    };
    
    if (elementType in headingSizes) {
      return headingSizes[elementType as keyof typeof headingSizes];
    }
    
    return 18;
  }

  // Update temp value when external value changes
  useEffect(() => {
    setTempValue(value);
  }, [value]);

  // Synchronize textStyles with initialStyles changes (for undo/redo)
  useEffect(() => {
    if (initialStyles && Object.keys(initialStyles).length > 0) {
      setTextStyles(prevStyles => ({
        ...getDefaultStyles(element),
        ...prevStyles,
        ...initialStyles
      }));
    }
  }, [initialStyles, element, isGlobalDarkMode, sectionBackgroundColor]);

  // Update text color when theme or background changes (if not explicitly set)
  useEffect(() => {
    // Don't auto-update color if it's explicitly set in initialStyles or if it's a special color (like primary brand color)
    if (!initialStyles.color && !textStyles.color?.startsWith('#')) {
      const defaultStyles = getDefaultStyles(element);
      setTextStyles(prevStyles => ({
        ...prevStyles,
        color: defaultStyles.color
      }));
    }
  }, [isGlobalDarkMode, initialStyles.color, sectionBackgroundColor, element]);

  // Handle external editing state
  useEffect(() => {
    if (externalIsEditing && !isEditing && editModeEnabled) {
      startEditing();
    } else if (!externalIsEditing && isEditing) {
      finishEditing();
    }
  }, [externalIsEditing, editModeEnabled]);

  // Control toolbar visibility based on hover, focus, and toolbar hover states
  useEffect(() => {
    // Clear any existing timeout
    if (fadeTimeout) {
      clearTimeout(fadeTimeout);
      setFadeTimeout(null);
    }

    const shouldShowToolbar = editModeEnabled && !isEditing && (isHovered || isSelected || isToolbarHovered);
    
    if (shouldShowToolbar) {
      // Show immediately
      setShowToolbar(true);
    } else if (showToolbar) {
      // Hide with 400ms delay
      const timeout = setTimeout(() => {
        setShowToolbar(false);
      }, 400);
      setFadeTimeout(timeout);
    }
  }, [editModeEnabled, isEditing, isHovered, isSelected, isToolbarHovered]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fadeTimeout) {
        clearTimeout(fadeTimeout);
      }
    };
  }, [fadeTimeout]);

  const startEditing = () => {
    if (!editModeEnabled) return;
    
    setIsEditing(true);
    setTempValue(value);
    onEditStart?.();
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        if (inputRef.current instanceof HTMLInputElement) {
          inputRef.current.select();
        } else if (inputRef.current instanceof HTMLTextAreaElement) {
          inputRef.current.select();
        }
      }
    }, 0);
  };

  const finishEditing = () => {
    setIsEditing(false);
    setIsSelected(false); // Ensure toolbar closes after editing
    onChange(tempValue);
    onEditEnd?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setTempValue(newValue);
    // Don't call onChange here - only call it when finishing editing
    // This preserves raw shortcodes during typing
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      finishEditing();
    } else if (e.key === 'Escape') {
      setTempValue(value);
      setIsEditing(false);
      onEditEnd?.();
    } else if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      finishEditing();
    }
  };

  const handleBlur = () => {
    finishEditing();
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing && editModeEnabled) {
      startEditing();
    }
  };

  const updateTextStyle = (key: string, value: any) => {
    const newStyles = { ...textStyles, [key]: value };
    setTextStyles(newStyles);
    onStyleChange?.(newStyles);
  };

  // Calculate toolbar position
  const calculateToolbarPosition = () => {
    if (textElementRef.current) {
      const rect = textElementRef.current.getBoundingClientRect();
      setToolbarPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  };

  // Handle mouse enter for toolbar
  const handleMouseEnter = () => {
    if (editModeEnabled && !isEditing) {
      setIsHovered(true);
      calculateToolbarPosition();
    }
  };

  // Handle mouse leave for toolbar
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Handle focus for toolbar
  const handleFocus = () => {
    if (editModeEnabled && !isEditing) {
      setIsSelected(true);
      calculateToolbarPosition();
    }
  };

  // Handle blur for toolbar
  const handleElementBlur = () => {
    setIsSelected(false);
  };

  // Handle toolbar hover state
  const handleToolbarMouseEnter = () => {
    setIsToolbarHovered(true);
  };

  const handleToolbarMouseLeave = () => {
    setIsToolbarHovered(false);
  };

  // Handle element type change from toolbar
  const handleElementTypeChange = (newElementType: string) => {
    // This would need to be passed up to parent component
    // For now, we'll just update font size based on element type
    const elementSizes: { [key: string]: number } = {
      'h1': 128, 'h2': 96, 'h3': 72, 'h4': 56, 'h5': 48, 'h6': 36,
      'p-large': 32, 'p-medium': 24, 'p': 18, 'p-small': 14
    };
    
    if (elementSizes[newElementType]) {
      updateTextStyle('fontSize', elementSizes[newElementType]);
    }
  };

  // Apply text transformation
  const applyTextTransform = (text: string, transform: string): string => {
    switch (transform) {
      case 'uppercase':
        return text.toUpperCase();
      case 'lowercase':
        return text.toLowerCase();
      case 'capitalize':
        return text.replace(/\b\w/g, l => l.toUpperCase());
      case 'title-case':
        return text.replace(/\w\S*/g, (txt) => 
          txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
      default:
        return text;
    }
  };

  const combinedStyles = {
    ...style,
    fontFamily: textStyles.fontFamily,
    fontSize: `${textStyles.fontSize}px`,
    textAlign: textStyles.textAlign,
    fontWeight: textStyles.fontWeight,
    fontStyle: textStyles.fontStyle,
    textDecoration: textStyles.textDecoration,
    textTransform: textStyles.textTransform,
    lineHeight: textStyles.lineHeight,
    letterSpacing: textStyles.letterSpacing,
    color: textStyles.color,
    backgroundColor: textStyles.backgroundColor === 'transparent' ? 'transparent' : textStyles.backgroundColor,
    whiteSpace: multiline ? 'pre-wrap' : 'normal',
  };

  const containerStyles = {
    position: 'relative' as const,
    display: 'inline-block',
    width: '100%',
    minHeight: isEditing ? 'auto' : undefined,
    overflow: 'visible'
  };

  const baseClassName = `
    w-full transition-all duration-200 ease-in-out
    ${editModeEnabled && isHovered && !isEditing ? (isGlobalDarkMode ? 'bg-blue-900/20' : 'bg-blue-50/30') : ''}
    ${isEditing ? (isGlobalDarkMode ? 'bg-gray-800/90 shadow-sm' : 'bg-white/90 shadow-sm') : ''}
    ${editModeEnabled && !isEditing ? 'cursor-text' : ''}
    ${editModeEnabled ? 'rounded-sm px-1 py-0.5' : ''}
    ${className}
  `;

  const editingClassName = `
    w-full bg-transparent border-none outline-none resize-none
    font-inherit text-inherit leading-inherit
    ${className}
  `;

  // Process shortcodes for display (only when not editing)
  const displayValue = useMemo(() => {
    let processedValue = value;
    
    // Process shortcodes first
    if (enableShortcodes && !isEditing && value) {
      processedValue = processText(value);
    }
    
    // Then apply text transform
    return applyTextTransform(processedValue, textStyles.textTransform);
  }, [enableShortcodes, isEditing, value, processText, textStyles.textTransform]);

  if (isEditing && editModeEnabled) {
    const InputComponent = multiline ? 'textarea' : 'input';
    
    return (
      <div ref={containerRef} style={containerStyles}>
        <InputComponent
          ref={inputRef as any}
          value={tempValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={editingClassName}
          style={{
            ...combinedStyles,
            minHeight: multiline ? '2em' : 'auto',
            padding: '2px 4px',
            margin: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent'
          }}
          placeholder={placeholder}
          rows={multiline ? Math.max(tempValue.split('\n').length, 2) : undefined}
        />
      </div>
    );
  }

  const Element = element as keyof JSX.IntrinsicElements;
  
  return (
    <div ref={containerRef} style={containerStyles}>
      <Element
        ref={textElementRef}
        className={baseClassName}
        style={{
          ...combinedStyles,
          margin: 0,
          padding: editModeEnabled ? '2px 4px' : '0',
          minHeight: 'auto',
          display: 'block',
          width: '100%'
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleElementBlur}
        tabIndex={editModeEnabled ? 0 : -1}
      >
        {displayValue || (editModeEnabled ? (
          <span className={`italic transition-colors duration-300 ${
            isGlobalDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            {placeholder}
          </span>
        ) : '')}
      </Element>

      {/* Text Toolbar */}
      <TextToolbar
        isVisible={showToolbar}
        position={toolbarPosition}
        textStyles={textStyles}
        onStyleChange={updateTextStyle}
        isGlobalDarkMode={isGlobalDarkMode}
        elementType={element}
        onElementTypeChange={handleElementTypeChange}
        textElementWidth={textElementRef.current?.offsetWidth || 0}
        textElementHeight={textElementRef.current?.offsetHeight || 0}
        onMouseEnter={handleToolbarMouseEnter}
        onMouseLeave={handleToolbarMouseLeave}
        originalTextStyles={originalStyles}
        getDefaultStyles={getDefaultStyles}
      />
    </div>
  );
};